
import DOMPurify from 'dompurify';

/**
 * Sanitise le contenu HTML pour éviter les attaques XSS
 * Utilisé principalement pour le contenu généré par l'IA
 */
export const sanitizeHTML = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Configuration DOMPurify pour permettre uniquement les balises sûres
  const cleanHTML = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['strong', 'em', 'br', 'p', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['style'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|#):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    // Permettre uniquement les styles de couleur pour les éléments strong
    ALLOW_DATA_ATTR: false,
    SANITIZE_DOM: true,
    KEEP_CONTENT: true
  });

  return cleanHTML;
};

/**
 * Sanitise le texte brut en échappant les caractères HTML dangereux
 */
export const sanitizeText = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Valide et nettoie les entrées utilisateur pour les formulaires
 */
export const sanitizeUserInput = (input: string, maxLength: number = 1000): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Supprimer les caractères de contrôle et limiter la longueur
  const cleaned = input
    .replace(/[\x00-\x1F\x7F]/g, '') // Supprimer les caractères de contrôle
    .trim()
    .substring(0, maxLength);

  return sanitizeText(cleaned);
};

/**
 * Valide le format email
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

/**
 * Valide le format téléphone (version plus permissive)
 */
export const isValidPhone = (phone: string): boolean => {
  // Plus permissif : accepter les numéros avec préfixes internationaux et différents formats
  const phoneRegex = /^[\+]?[\d\s\-\(\)\.]{8,20}$/;
  const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
  return phoneRegex.test(phone) && cleanPhone.length >= 8;
};
